import defaultTheme from "./default";
import nimbleTheme from "./nimble";
import { fgfConfig } from "../../config/fgf";
import { nimbleConfig } from "../../config/nimble";

const fgfParent = {
  default: {
    themeName: "fgfTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
  yesLoans: {
    themeName: "yesLoansTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
  rent: {
    themeName: "rentBondTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
  clearScore: {
    themeName: "clearScoreTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
  overflow: {
    themeName: "overflowTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
  loanOptions : {
    themeName: "loanOptionsTheme",
    file: defaultTheme,
    configFile: fgfConfig
  },
} as const;

export type FgfParent = typeof fgfParent;

export const nimbleParent = {
  default: {
    themeName: "nimbleTheme",
    file: nimbleTheme,
    configFile: nimbleConfig
  },
  zeefi: {
    themeName: "zeefiTheme",
    file: defaultTheme,
    configFile: nimbleConfig
  },
} as const;

export type NimbleParent = typeof nimbleParent;

export const defaultFallback = {
  themeName: "defaultFallbackTheme",
  file: defaultTheme,
  configFile: fgfConfig
} as const;

export type DefaultFallback = typeof defaultFallback;
export type DefaultFallbackThemeName = typeof defaultFallback['themeName'];

const loading = {
  themeName:"loading",
  file: {},
  configFile: {}
} as const;

export type Loading = typeof loading;
export type LoadingThemeName = typeof loading['themeName'];

export const themes = {
  fgfParent,
  nimbleParent,
  defaultFallback,
  loading
} as const

export type Themes = typeof themes;

export type Theme = {
  themeName: ThemeNames;
  file: {};
  configFile: {};
};
export type Brands = keyof typeof themes;
export type ThemeKeys = {
  [key in Brands]: key
};
  
export const brands: Brands[] = Object.keys(themes) as Brands[];

export type NestedKeys<T extends Brands> = keyof typeof themes[T];

export type FgfParentKeys = NestedKeys<'fgfParent'>;
export const fgfParentKeys: FgfParentKeys[] = Object.keys(themes.fgfParent) as FgfParentKeys[];

export type NimbleParentKeys = NestedKeys<'nimbleParent'>;
export const nimbleParentKeys: NimbleParentKeys[] = Object.keys(themes.nimbleParent) as NimbleParentKeys[];

export type QueryThemes = NimbleParentKeys | FgfParentKeys
export const queryThemes = [...new Set([...fgfParentKeys, ...nimbleParentKeys])]

export type ThemeNames = 
  | FgfParent[keyof typeof fgfParent]['themeName']
  | NimbleParent[keyof typeof nimbleParent]['themeName']
  | DefaultFallback['themeName']
  | Loading['themeName'];

export type FgfThemes = FgfParent[keyof typeof fgfParent]['themeName'];
export const fgfThemes: Array<FgfThemes> = [themes.fgfParent.default.themeName, themes.fgfParent.rent.themeName, themes.fgfParent.clearScore.themeName, themes.fgfParent.overflow.themeName, themes.fgfParent.yesLoans.themeName]
export const fgfChildThemes: Array<FgfThemes> = [themes.fgfParent.rent.themeName, themes.fgfParent.clearScore.themeName, themes.fgfParent.overflow.themeName, themes.fgfParent.yesLoans.themeName]

export type NimbleThemes = NimbleParent[keyof typeof nimbleParent]['themeName'];
export const nimbleThemes: Array<NimbleThemes> = [themes.nimbleParent.default.themeName, themes.nimbleParent.zeefi.themeName]
export const nimbleChildThemes: Array<NimbleThemes> = [themes.nimbleParent.zeefi.themeName]


export const isBrand = (state: any) => state.StartUp.queryTheme === 'default' || !state.StartUp.queryTheme;

const foo: Theme = {
  themeName: "clearScoreTheme",
  file: {},
  configFile: {}
}

export const determineTheme = (brand: Brands, queryTheme?: QueryThemes): Theme => {
  // Helper function to get a theme by key safely
  const getDefaultTheme = (): Theme => themes['defaultFallback'];
  const getLoadingTheme = (): Theme => themes['loading'];

  // Handling the special cases
  if (brand === "loading") {
    return getLoadingTheme();
  }
  if (brand === "defaultFallback") {
    return getDefaultTheme();
  }

  // Check if the parent theme exists and then process child themes
  if (Object.hasOwn(themes, brand)) {
    const brandObject = themes[brand];
    if (queryTheme) {
      const queryThemeLowerCase = queryTheme.toLowerCase();
      const childThemeKey = Object.keys(brandObject).find(
        (key) => key.toLowerCase() === queryThemeLowerCase
      );

      if (childThemeKey) {
        return brandObject[childThemeKey as keyof typeof brandObject];
      }
    }
    // Return the default theme if no matching queryTheme is found
    return brandObject["default"] ?? getDefaultTheme();
  }

  // Fallback case when brand is not found
  return getDefaultTheme();
};

const hasNonEmptyHost = (host?: string): host is string => {
  return (!!host && host.length > 0);
}
const isNimbleTheme = (host: string): host is NimbleThemes => {
  return host.includes('nimble'); 
}
const isFgfTheme = (host: string): host is FgfThemes => {
  return host.includes('fairgofinance') || host.includes('fgf') || host.includes('fairgo');
}

const determineBrand = (host: string): Brands => {
  if (isNimbleTheme(host)) {
    return 'nimbleParent';
  }
  if (isFgfTheme(host)) {
    return 'fgfParent';
  } 
  return 'defaultFallback';
}

export const getBrandFromHost = (host?: string): Brands => {
  const hasHost = hasNonEmptyHost(host);
  if (hasHost) {
    return determineBrand(host);
  }
  return 'defaultFallback';
};

type QueryString = {theme?: string}
export const getThemeFromQuerystring = (queryString: QueryString) => {
  return queryString.theme;
};
